import { useCallback, useEffect, useState } from 'react'
import {
  acceptJoinRequest as acceptJoinRequestApi,
  declineJoinRequest as declineJoinRequestApi,
  loadJoinRequests,
  updateJoinPolicy,
} from '../api/leagues'
import type { League, LeagueJoinPolicy, LeagueJoinRequest } from '../types'

export interface UseLeagueJoinRequestsOptions {
  /** The open league's detail screen, or null in the tab shell. */
  leagueId: string | null
  /** Only the owner may read the list, so a member's screen never asks. */
  isOwner: boolean
  onToast: (message: string) => void
  /** Patches the league in the main list after an accept or a policy change. */
  onLeagueUpdated: (league: League) => void
}

export interface LeagueJoinRequestsController {
  /** Pending asks on the open league. Always empty for a non-owner. */
  requests: LeagueJoinRequest[]
  accept: (requestId: string) => Promise<void>
  decline: (requestId: string) => Promise<void>
  setPolicy: (joinPolicy: LeagueJoinPolicy) => Promise<void>
}

/**
 * The owner's side of ask-to-join: who is waiting, and the policy that decides
 * whether anyone can wait at all.
 *
 * Scoped to whichever league detail is open rather than fetched for every league the user owns —
 * the badge only has to be right on the screen showing it, and one request per league open is
 * cheaper than one per league owned on every app load.
 */
export function useLeagueJoinRequests({
  leagueId,
  isOwner,
  onToast,
  onLeagueUpdated,
}: UseLeagueJoinRequestsOptions): LeagueJoinRequestsController {
  // Stored with the league it belongs to, so switching leagues shows nothing until the new list
  // lands rather than briefly showing the previous league's requests — and so the effect below
  // never has to clear state synchronously on the way past.
  const [loaded, setLoaded] = useState<{ leagueId: string; rows: LeagueJoinRequest[] }>({
    leagueId: '',
    rows: [],
  })
  const requests = loaded.leagueId === leagueId ? loaded.rows : []

  const refresh = useCallback(() => {
    if (!leagueId || !isOwner) return Promise.resolve()
    return loadJoinRequests(leagueId)
      .then((rows) => setLoaded({ leagueId, rows }))
      .catch(() => {
        // Silent: an empty request list is the same screen as a failed fetch, and there is nothing
        // the owner could do about it here anyway.
        setLoaded({ leagueId, rows: [] })
      })
  }, [leagueId, isOwner])

  const patchRows = (update: (rows: LeagueJoinRequest[]) => LeagueJoinRequest[]) =>
    setLoaded((prev) => ({ ...prev, rows: update(prev.rows) }))

  useEffect(() => {
    void refresh()
  }, [refresh])


  return {
    requests,

    accept: async (requestId) => {
      if (!leagueId) return
      try {
        const league = await acceptJoinRequestApi(leagueId, requestId)
        patchRows((rows) => rows.filter((r) => r.id !== requestId))
        onLeagueUpdated(league)
        onToast('Request accepted.')
      } catch {
        // Covers the 409 too: they joined some other way, so the row is stale either way and the
        // server has already dropped it.
        await refresh()
        onToast("Couldn't accept that request. Please try again.")
      }
    },

    decline: async (requestId) => {
      if (!leagueId) return
      try {
        await declineJoinRequestApi(leagueId, requestId)
        patchRows((rows) => rows.filter((r) => r.id !== requestId))
      } catch {
        onToast("Couldn't decline that request. Please try again.")
      }
    },

    setPolicy: async (joinPolicy) => {
      if (!leagueId) return
      try {
        const league = await updateJoinPolicy(leagueId, joinPolicy)
        onLeagueUpdated(league)
        // Invite-only declines whatever was pending, server-side — mirror that here rather than
        // leaving rows on screen that no longer exist.
        if (joinPolicy === 'invite') patchRows(() => [])
      } catch {
        onToast("Couldn't change who can join. Please try again.")
      }
    },
  }
}
